import React from "react";

/**
 * Iter 32 — Trading Health Score gauge.
 * SVG circular progress with score in the center. Uses design-system color
 * tokens via the `color` prop (`green` | `amber` | `red`).
 *
 * Props:
 *   - score: number 0-100 (or null when locked)
 *   - color: "green" | "amber" | "red"
 *   - locked: boolean
 *   - label: band label ("Elite", "Strong", "Developing", "At Risk", "Critical")
 *   - message: one-line band message
 *   - reliability: "Early estimate" | "Reliable"
 *   - tradesLogged: number — used in locked state
 *   - tradesNeeded: number — used in locked state
 */
export function HealthScoreGauge({
  score = null, color = "amber", locked = false,
  label = "", message = "", reliability = "",
  tradesLogged = 0, tradesNeeded = 5,
}) {
  const size = 200;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = locked
    ? (tradesLogged / Math.max(1, tradesNeeded)) * 100
    : Math.max(0, Math.min(100, score || 0));
  const dashOffset = circumference - (pct / 100) * circumference;

  // Map color name → CSS variable for the gauge fill stroke + accent
  const fill =
    color === "green" ? "var(--bt-win)" :
    color === "red"   ? "var(--bt-loss)" :
                        "var(--bt-warn)";
  const fillSoft =
    color === "green" ? "color-mix(in srgb, var(--bt-win) 25%, transparent)" :
    color === "red"   ? "color-mix(in srgb, var(--bt-loss) 25%, transparent)" :
                        "color-mix(in srgb, var(--bt-warn) 25%, transparent)";

  return (
    <section
      data-testid="health-score-section"
      className="bt-card p-6 sm:p-7 flex flex-col sm:flex-row items-center sm:items-stretch gap-6"
    >
      {/* ── Gauge ── */}
      <div
        className="relative flex-shrink-0"
        style={{ width: size, height: size }}
        data-testid="health-score-gauge"
      >
        <svg
          width={size} height={size} viewBox={`0 0 ${size} ${size}`}
          style={{ transform: "rotate(-90deg)" }}
        >
          {/* Track */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="var(--bt-border)" strokeWidth={stroke} fill="none"
          />
          {/* Progress */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke={locked ? "var(--bt-text-muted)" : fill}
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{
              transition: "stroke-dashoffset 720ms cubic-bezier(0.22, 1, 0.36, 1)",
              filter: locked ? "none" : `drop-shadow(0 0 8px ${fillSoft})`,
            }}
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          {locked ? (
            <>
              <span aria-hidden="true" className="text-3xl mb-1">🔒</span>
              <span
                data-testid="health-score-locked-progress"
                className="bt-num text-base"
                style={{ color: "var(--bt-text-muted)" }}
              >
                {tradesLogged}/{tradesNeeded}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[var(--bt-text-muted)] mt-0.5">
                logged
              </span>
            </>
          ) : (
            <>
              <span
                data-testid="health-score-value"
                className="font-heading bt-num"
                style={{
                  fontSize: 64, lineHeight: 1, color: fill, fontWeight: 800,
                }}
              >
                {score}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[var(--bt-text-muted)] mt-1">
                / 100
              </span>
            </>
          )}
        </div>
      </div>

      {/* ── Right side: label + message ── */}
      <div className="flex-1 flex flex-col justify-center text-center sm:text-left">
        <p className="bt-label flex items-center gap-2 justify-center sm:justify-start">
          <span aria-hidden="true">📊</span>
          Trading Health Score
          {!locked && reliability && (
            <span
              data-testid="health-score-reliability"
              className="text-[10px] font-normal normal-case tracking-normal px-2 py-0.5 rounded-full border"
              style={{
                color: "var(--bt-text-muted)",
                borderColor: "var(--bt-border)",
                background: "var(--bt-surface-2)",
              }}
            >
              {reliability}
            </span>
          )}
        </p>

        {locked ? (
          <>
            <p className="font-heading text-2xl sm:text-3xl mt-3" data-testid="health-score-band-label">
              Locked
            </p>
            <p className="text-sm text-[var(--bt-text-muted)] mt-1.5" data-testid="health-score-message">
              {message}
            </p>
          </>
        ) : (
          <>
            <p
              className="font-heading text-3xl sm:text-4xl mt-3"
              style={{ color: fill }}
              data-testid="health-score-band-label"
            >
              {label}
            </p>
            <p className="text-sm text-[var(--bt-text-muted)] mt-1.5" data-testid="health-score-message">
              {message}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

export default HealthScoreGauge;
